import { useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import {
  Button,
  Checkbox,
  Modal,
  Phone,
  Text,
  Input,
  DatePicker,
} from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, Input as MantineInput, rem } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { showNotification } from 'components/molecules/notificationsHandler'
import { SuccessBox } from 'components/molecules/successBox'
import { verifyPhoneExistByCampaignCodeApi } from 'components/pages/healthTools/frisoOnboarding/helper'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { GA_TOKEN_COOKIE, MANTINE_MODAL_TARGET } from 'interfaces/constants'

import { generateSchema } from './helpers'
import useStyles from './index.styles'
import { LeadgenWithBannerProps } from '..'

export type FormProps = {
  title: string
  description?: string
  ctaTitle?: string
  campaignCode: string
  thankYouMessage: LeadgenWithBannerProps['data']['thankYouMessage']
  primaryColor?: string
  onDone?: () => void
} & BoxProps

type FormField = {
  name: string
  child_dob: string
  phone: string
  term: boolean
}

export const SoyaForm = ({
  title,
  description,
  ctaTitle,
  campaignCode,
  styles,
  classNames,
  className,
  primaryColor,
  thankYouMessage,
  onDone,
}: FormProps) => {
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const { classes, cx } = useStyles(undefined, {
    name: 'LeadgenWithBannerForm',
    styles,
    classNames,
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const form = useForm({
    validate: yupResolver(generateSchema(f)),
  })
  const onSubmit = (values: FormField) => {
    if (!campaignCode) return
    const formatValues = {
      ...values,
      child_dob: values.child_dob
        ? dayjs(values.child_dob).format('YYYY-MM-DD')
        : '',
    }
    setLoading(true)
    verifyPhoneExistByCampaignCodeApi(values.phone, campaignCode).then(
      async (response) => {
        if (response?._messages?.[0] === 'Phone was exist') {
          form.setFieldError(
            'phone',
            f({
              id: 'carePathLead.form.existingVoucher',
              defaultMessage: 'This phone received the voucher.',
            }),
          )
          setLoading(false)
        } else {
          const res = await callApiNew(
            PATHS.LEADGEN.POST_LEAD_LEAD,
            {
              method: 'POST',
              params: {
                campaignCode,
              },
              data: {
                url: window.location.href,
                title_article: document.title,
                referrer: window.location.href,
                meta: Object.keys(formatValues)
                  .filter((i) => i !== 'term')
                  .map((i) => ({
                    key: i,
                    value: (formatValues as any)[i],
                    control: 'text',
                  })),
                ga_client_id: Cookies.get(GA_TOKEN_COOKIE),
              },
            },
            'lead',
          )
          if (res?._status === 1) {
            setShowSuccess(true)
            form.setValues({
              name: '',
              child_dob: null,
              email: '',
              phone: '',
              term: false,
            })
          } else {
            showNotification({
              title: f({ id: 'notification.comment.somethingWrong' }),
              message: '',
              type: 'danger',
            })
          }
          setLoading(false)
        }
      },
    )
  }
  return (
    <>
      <Box className={cx(classes.root, className)}>
        <form
          className={classes.wrapper}
          onSubmit={form.onSubmit(onSubmit as any)}
        >
          {title && <Heading tag="h3">{title}</Heading>}
          {description && <Text size="p3">{description}</Text>}
          <Box className={classes.controlList}>
            <Input
              className={cx(classes.control, classes.nameControl)}
              data-event-category="Chatbot"
              data-event-action="Form Filling"
              data-event-label={currentUrl}
              label={
                <Text size="s4" as="label">
                  {f({
                    id: 'carePathLead.form.name',
                    defaultMessage: `Name`,
                  })}
                </Text>
              }
              withAsterisk
              {...form.getInputProps('name')}
            />
            <MantineInput.Wrapper
              className={classes.control}
              withAsterisk
              label={
                <Text size="s4" as="label">
                  {f({
                    id: 'carePathLead.form.phone',
                    defaultMessage: 'Phone number',
                  })}
                </Text>
              }
            >
              <Phone
                defaultCountry={'ID'}
                data-event-category="Chatbot"
                data-event-action="Form Filling"
                data-event-label={currentUrl}
                {...form.getInputProps('phone')}
              />
              <MantineInput.Error size="md">
                {form.errors.phone}
              </MantineInput.Error>
            </MantineInput.Wrapper>
            <DatePicker
              data-event-category="Chatbot"
              data-event-action="Form Filling"
              data-event-label={currentUrl}
              classNames={{
                root: classes.control,
              }}
              label={
                <Text size="s4" as="label">
                  {f({
                    id: 'carePathLead.form.dob',
                    defaultMessage: 'Child of DOB ',
                  })}
                </Text>
              }
              placeholder="DD/MM/YYYY"
              maxDate={dayjs().toDate()}
              popoverProps={{
                ...(!isMobile && {
                  withinPortal: true,
                  portalProps: {
                    target: `#datepicker-popover-target`,
                  },
                }),
              }}
              {...form.getInputProps('child_dob')}
            />
            <Box className={classes.actionList}>
              <Checkbox
                classNames={{
                  error: classes.controlErr,
                }}
                label={
                  <div>
                    Saya sudah membaca dan menyetujui{' '}
                    <a
                      href="https://www.generasimaju.co.id/event-dan-promo/promo/program-promo-voucher-sgm-soya-dengan-hello-sehat"
                      target="_blank"
                    >
                      Syarat dan Ketentuan Program.
                    </a>
                  </div>
                }
                color={primaryColor}
                data-event-category="Chatbot"
                data-event-action="Form Filling"
                data-event-label={currentUrl}
                {...form.getInputProps('term', { type: 'checkbox' })}
                sx={{
                  a: {
                    color: primaryColor,
                  },
                }}
              />
              <Button
                className={classes.submitBtn}
                size={isMobile ? 'md' : 'lg'}
                variant="primary"
                type="submit"
                loading={loading}
                color={primaryColor}
                fullWidth={isMobile}
                data-event-category="Chatbot"
                data-event-action="Form Submit"
                data-event-label={currentUrl}
              >
                {ctaTitle || f({ id: 'carePathLead.form.submitBtn' })}
              </Button>
            </Box>
          </Box>
          {/* <Box className={classes.actionList}>
            <Text size="n1" className={classes.hint}>
              {f({
                id: 'carePathLead.form.requiredAllField',
                defaultMessage: 'All fields are required',
              })}
            </Text>
            
          </Box> */}
        </form>
      </Box>
      <Modal
        target={MANTINE_MODAL_TARGET}
        size={rem(408)}
        opened={showSuccess}
        withCloseButton
        zIndex={303}
        styles={{
          header: {
            border: 0,
            paddingBottom: 0,
          },
          body: {
            padding: rem(32),
          },
        }}
        onClose={() => {
          setShowSuccess(false)
          onDone?.()
        }}
      >
        <SuccessBox
          heading={thankYouMessage.title}
          description={thankYouMessage.description}
          onClose={() => {
            setShowSuccess(false)
            onDone?.()
          }}
          styles={{
            button: {
              width: '100%',
              '&[data-variant="primary"],&[data-primary-btn="true"]': {
                '&:not(:disabled)': {
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                  },
                },
              },
            },
          }}
        />
      </Modal>
    </>
  )
}
